import { isLocalHost } from './isLocalHost';

/**
 * Console output, developer machine and the kiosk unit only.
 *
 * Everything this app logs is diagnostic: lens IDs, device IDs, MIME
 * negotiation, upload failures, queue state. None of it means anything to a
 * guest and all of it is readable by anyone who opens devtools on the deployed
 * site, so it is tied to where the page is being read from rather than to the
 * build mode — a dev build on a preview URL would otherwise still print it.
 *
 * ## Why the kiosk is included
 *
 * It was localhost only, which silenced the unit — where the shell serves from
 * appassets.androidplatform.net. That made `chrome://inspect` and
 * `adb logcat` useless in exactly the week they matter most, because the native
 * shell mirrors every console line into logcat (see KioskChromeClient). Nobody
 * is looking at a console on a 49" panel, so there is no guest-facing cost, and
 * the operator gets a real trace.
 *
 * `?verbose=0` (or `localStorage['incomer:verbose'] = '0'`) turns it back off on
 * the unit if the noise ever gets in the way; `'1'` turns it on anywhere else,
 * which is how you debug a real deployed URL without shipping a build.
 *
 * Decided once at module load. The hostname cannot change without a navigation,
 * and a per-call check would run inside the render loop.
 *
 * Note this silences errors too when off. They are not lost: noteError still
 * records every one into the diagnostics ring the admin view reads.
 */

function override(): boolean | null {
  // Wrapped because a WebView with site data blocked throws on access, and a
  // logging helper must never be the thing that stops the app booting.
  try {
    const q = new URLSearchParams(window.location.search).get('verbose');
    if (q === '1') return true;
    if (q === '0') return false;
    const stored = window.localStorage.getItem('incomer:verbose');
    if (stored === '1') return true;
    if (stored === '0') return false;
  } catch {
    /* no storage, no query — fall through to the default */
  }
  return null;
}

/** True inside the native Android shell. Mirrors src/config/kioskShell.ts
 *  without importing it, so this module keeps zero app dependencies. */
function inKioskShell(): boolean {
  try {
    const s = (globalThis as { __KIOSK__?: { shell?: string } }).__KIOSK__;
    return s?.shell === 'android';
  } catch {
    return false;
  }
}

const on = override() ?? (isLocalHost() || inKioskShell());

export function devLog(...args: unknown[]): void {
  if (on) console.log(...args);
}

export function devWarn(...args: unknown[]): void {
  if (on) console.warn(...args);
}

export function devError(...args: unknown[]): void {
  if (on) console.error(...args);
}
